import React, { useEffect, useState } from 'react';
import { useLocale } from 'next-intl';

import { TeamBlockProps } from '../../_blocks/TeamBlockGraphQL/types';
import { fetchTeam } from '../../_api/fetchDocs';
import { Person, Media } from '@/payload/payload-types';
import { Department, TeamDepartment } from './Department';
import classes from './TeamBlock.module.scss';
import { DebugJsonBlock } from '../DebugJsonBlock';
import SquareImageWithLabel from '../SquareImageWithLabel';
import { Gutter } from '../Gutter';
import { ResimdoContactInfo } from '@/app/_components/ResimdoContactInfo';

type TeamByDepartment = Record<TeamDepartment, Person[]>;

const groupTeamByDepartment = (team: Person[]): TeamByDepartment => {
  return team.reduce((acc, person) => {
    const department = person.department || 'management'; // Default to management if no department
    if (!acc[department]) {
      acc[department] = [];
    }
    acc[department].push(person);
    return acc;
  }, {} as TeamByDepartment);
};

export const TeamBlock: React.FC<{
  className?: string;
  teamBlock: TeamBlockProps;
}> = ({
  className,
  teamBlock,
}) => {
  const [team, setTeam] = useState<TeamByDepartment>({} as TeamByDepartment);
  const locale = useLocale();
  const contactHeadingText = teamBlock.contactHeading?.text || teamBlock.contactHeading_legacy || '';

  useEffect(() => {
    fetchTeam(locale)
      .then((teamResponse) => {
        const visibleTeam = teamResponse.filter((person) => person.visible !== false);
        const groupedTeam = groupTeamByDepartment(visibleTeam);
        setTeam(groupedTeam);
      })
      .catch(console.error);
  }, [locale]);

  return team && Object.keys(team).length > 0 ? (
    <Gutter className={[className, classes.wrapper].filter(Boolean).join(' ')}>
      <span className={`${classes.mobileOnly} ${classes.contactInfo}`}>
        <ResimdoContactInfo title={contactHeadingText} />
      </span>
      {team.advisory && (
        <div className={classes.departmentSection}>
          <h2 className={classes.departmentTitle}>Advisory</h2>
          <Department team={team.advisory} firstModule={true} showContactInfo={true} contactHeading={contactHeadingText} />
        </div>
      )}
      {team.logistics && (
        <div className={classes.departmentSection}>
          <h2 className={classes.departmentTitle}>Logistics</h2>
          <Department team={team.logistics} />
        </div>
      )}
      {team.marketing && (
        <div className={classes.departmentSection}>
          <h2 className={classes.departmentTitle}>Marketing</h2>
          <Department team={team.marketing} />
        </div>
      )}
      {team.management && (
        <span className={classes.desktopOnly}>
          <div className={classes.departmentSection}>
            <h2 className={classes.departmentTitle}>Management</h2>
            <div className={classes.managementGrid}>
              <div className={classes.halfWidthDepartment}>
                {team.management[0] && <SquareImageWithLabel
                  image={team.management[0].image as Media}
                  label={team.management[0].nickname || team.management[0].firstName}
                />}
              </div>
              {team.cvo && team.cvo[0] && (
                <>
                  <div className={classes.cvoTitleCell}>
                    <h2 className={[classes.departmentTitle, classes.cvoTitle].filter(Boolean).join(' ')}>CVO</h2>
                  </div>
                  <div className={classes.halfWidthDepartment}>
                    <SquareImageWithLabel
                      image={team.cvo[0].image as Media}
                      label={team.cvo[0].nickname || team.cvo[0].firstName}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </span>
      )}
      <span className={classes.mobileOnly}>
        {team.management && (
          <div className={classes.departmentSection}>
            <h2 className={classes.departmentTitle}>Management</h2>
            <Department team={team.management} />
          </div>
        )}
        {team.cvo && (
          <div className={classes.departmentSection}>
            <h2 className={classes.departmentTitle}>CVO</h2>
            <Department team={team.cvo} />
          </div>
        )}
      </span>
      <DebugJsonBlock block={team} label="Team" />
    </Gutter>
  ) : null;
  };

export default TeamBlock;
